import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CreditCard, X } from 'lucide-react';

/**
 * Global usage-limit wall.
 *
 * Mount once (e.g. inside DashboardLayout). It attaches an axios response
 * interceptor that detects 403 responses whose body matches:
 *     { detail: { error_code: "usage_limit_reached", ...} }
 * When it fires, a full-screen modal appears with the limit hit + an
 * Upgrade CTA. The user can dismiss to keep browsing read-only surfaces,
 * but any action that triggered the block will re-fire the wall.
 */
const UsageLimitWall = () => {
  const [block, setBlock] = useState(null);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      (err) => {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 403 && detail && typeof detail === 'object' && detail.error_code === 'usage_limit_reached') {
          setBlock(detail);
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  if (!block) return null;

  const pct = block.limit > 0 ? Math.min(100, Math.round((block.current / block.limit) * 100)) : 100;

  return (
    <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="usage-limit-wall">
      <div className="bg-slate-900 border border-amber-500/40 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl">
        <button onClick={() => setBlock(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="usage-wall-close" aria-label="Close"><X className="w-5 h-5" /></button>
        <div className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="text-2xl font-semibold text-white">You've reached your plan limit</h2>
          <p className="text-sm text-slate-400 mt-2">
            Your <span className="capitalize text-amber-300">{block.plan_type || 'Free'}</span> plan is capped at
            <b className="text-white"> {Number(block.limit || 0).toLocaleString()} {block.resource}/month</b>.
          </p>

          {/* Usage bar */}
          <div className="mt-5">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Current usage</span>
              <span className="font-mono">{Number(block.current || 0).toLocaleString()} / {Number(block.limit || 0).toLocaleString()}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 to-rose-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="mt-6 text-left bg-slate-950 border border-slate-800 rounded-lg p-4 text-sm text-slate-300">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-400 shrink-0" />
              <div>
                <div className="font-medium text-white">What's blocked</div>
                <div className="text-xs text-slate-400 mt-1">
                  {block.message || `New ${block.resource} creation is disabled until you upgrade or next billing cycle.`}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-2">
            <a
              href="/billing"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold"
              data-testid="usage-wall-upgrade"
            >
              <CreditCard className="w-4 h-4" /> Upgrade plan
            </a>
            <button
              onClick={() => setBlock(null)}
              className="px-4 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageLimitWall;
